import React, { useState, useEffect } from 'react';
import { 
  Leaf, 
  Users, 
  Target, 
  Trophy, 
  ArrowRight, 
  Play, 
  CheckCircle, 
  Globe, 
  Zap, 
  Heart,
  TreePine,
  Droplets,
  Recycle,
  Star,
  ChevronRight,
  Mail,
  Lock,
  User,
  Loader2
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useImpactTypes } from '../hooks/useImpactTypes';
import { useMissions } from '../hooks/useMissions';

export default function LandingPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const registrationRef = React.useRef<HTMLDivElement>(null);

  const { signUp, signIn } = useAuth();
  const { impactTypes, loading: impactLoading } = useImpactTypes();
  const { missions, loading: missionsLoading } = useMissions();

  const scrollToRegistration = () => {
    setIsSignUp(true);
    setTimeout(() => {
      registrationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isSignUp) {
        const { error } = await signUp(email, password, username);
        if (error) {
          setError(error.message);
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          setError(error.message);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Format value for display
  const formatValue = (value: number, unit: string) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M${unit ? ` ${unit}` : ''}`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K${unit ? ` ${unit}` : ''}`;
    }
    return `${value.toLocaleString()}${unit ? ` ${unit}` : ''}`;
  };

  // Dynamic stats from configured active impact types
  const dynamicStats = impactLoading ? [] : impactTypes
    .filter(type => type.active) // Only show active impact types
    .slice(0, 4)
    .map(type => {
      const IconComponent = (LucideIcons as any)[type.icon] || Target;
      return {
        value: formatValue(type.value, type.unit),
        label: type.label,
        icon: IconComponent
      };
    });

  // Fallback stats if no impact types are loaded
  const fallbackStats = [
    { value: '50K+', label: 'Eco Warriors', icon: Users },
    { value: '2.3M', label: 'Missions Completed', icon: Target },
    { value: '13K kg', label: 'CO₂ Saved', icon: Leaf },
    { value: '1K', label: 'Trees Planted', icon: TreePine }
  ];

  const displayStats = dynamicStats.length > 0 ? dynamicStats : fallbackStats;

  const features = [
    {
      icon: Target,
      title: 'Daily Eco Missions',
      description: 'Complete fun, achievable environmental challenges that fit into your daily routine'
    },
    {
      icon: Trophy,
      title: 'Earn Rewards',
      description: 'Collect EcoCoins and unlock real-world rewards, discounts, and exclusive badges'
    },
    {
      icon: Users,
      title: 'Join Community',
      description: 'Connect with like-minded eco-warriors and participate in local environmental events'
    },
    {
      icon: Globe,
      title: 'Track Impact',
      description: 'See your real environmental impact with detailed statistics and global contribution metrics'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Environmental Educator',
      avatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop',
      quote: 'Yashnar made sustainability fun and achievable. I\'ve reduced my carbon footprint by 40% in just 3 months!'
    },
    {
      name: 'Marcus Rodriguez',
      role: 'College Student',
      avatar: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop',
      quote: 'The gamification aspect keeps me motivated. I\'ve completed over 200 missions and earned enough coins for amazing rewards!'
    },
    {
      name: 'Emma Thompson',
      role: 'Community Organizer',
      avatar: 'https://images.pexels.com/photos/1040880/pexels-photo-1040880.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop',
      quote: 'Through Yashnar, I organized 5 community clean-ups. It\'s incredible how the platform brings people together for environmental action.'
    }
  ];

  // Get sample missions from database
  const missionExamples = missions.slice(0, 3).map(mission => {
    const IconComponent = (LucideIcons as any)[mission.icon] || Target;
    return {
      title: mission.title,
      description: mission.description,
      points: mission.points,
      difficulty: mission.difficulty.charAt(0).toUpperCase() + mission.difficulty.slice(1),
      icon: IconComponent,
      color: mission.difficulty === 'easy' ? 'bg-green-100 text-green-600' :
             mission.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-600' :
             'bg-red-100 text-red-600'
    };
  });

  // Fallback mission examples if database is empty
  const fallbackMissionExamples = [
    {
      title: 'Bring Your Own Bag',
      description: 'Use reusable bags for shopping',
      points: 50,
      difficulty: 'Easy',
      icon: Recycle,
      color: 'bg-green-100 text-green-600'
    },
    {
      title: 'Plant-Based Meal',
      description: 'Share a photo of your meat-free meal',
      points: 75,
      difficulty: 'Easy',
      icon: Leaf,
      color: 'bg-emerald-100 text-emerald-600'
    },
    {
      title: 'Community Clean-Up',
      description: 'Organize a local environmental event',
      points: 300,
      difficulty: 'Hard',
      icon: Users,
      color: 'bg-blue-100 text-blue-600'
    }
  ];

  const displayMissions = missionExamples.length > 0 ? missionExamples : fallbackMissionExamples;

  // Eco Journey Timeline Steps
  const journeySteps = [
    {
      id: 1,
      title: 'Sign Up',
      description: 'Create your account and join the eco-warrior community',
      icon: User,
      color: 'from-emerald-400 to-teal-500',
      bgColor: 'bg-emerald-100',
      iconColor: 'text-emerald-600'
    },
    {
      id: 2,
      title: 'Choose Missions',
      description: 'Browse daily, weekly, and monthly eco-challenges',
      icon: Target,
      color: 'from-blue-400 to-indigo-500',
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      id: 3,
      title: 'Take Action',
      description: 'Complete missions and document your eco-friendly actions',
      icon: Zap,
      color: 'from-yellow-400 to-orange-500',
      bgColor: 'bg-yellow-100',
      iconColor: 'text-yellow-600'
    },
    {
      id: 4,
      title: 'Earn Rewards',
      description: 'Collect EcoCoins and unlock exclusive rewards',
      icon: Trophy,
      color: 'from-purple-400 to-pink-500',
      bgColor: 'bg-purple-100',
      iconColor: 'text-purple-600'
    },
    {
      id: 5,
      title: 'Track Impact',
      description: 'Monitor your environmental contribution and progress',
      icon: Globe,
      color: 'from-green-400 to-emerald-500',
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <div className="bg-emerald-500 p-2 rounded-lg">
                <Leaf className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Yashnar</h1>
                <p className="text-xs text-gray-500 hidden sm:block">Gamify Your Impact</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsSignUp(false)}
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => setIsSignUp(true)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-50 via-teal-50 to-blue-50 py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2310b981' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="h-4 w-4 mr-2" />
                Join {impactTypes.find(t => t.name === 'total_users')?.value ? 
                  formatValue(impactTypes.find(t => t.name === 'total_users')!.value, '') : 
                  '19'} Eco Warriors Worldwide
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Turn Your
                <span className="text-emerald-600"> Green Actions</span>
                <br />
                Into Real Impact
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Join the gamified platform that makes environmental action fun, rewarding, and measurable. 
                Complete daily eco-missions, earn rewards, and create lasting change for our planet.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={scrollToRegistration}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex items-center justify-center space-x-2"
                >
                  <span>Start Your Eco Journey</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
                
                <button 
                  onClick={scrollToRegistration}
                  className="border-2 border-gray-300 hover:border-emerald-500 text-gray-700 hover:text-emerald-600 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <Play className="h-5 w-5" />
                  <span>Watch Demo</span>
                </button>
              </div>
            </div>
            
            {/* Auth Form */}
            <div ref={registrationRef} className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {isSignUp ? 'Join Yashnar' : 'Welcome Back'}
                </h3>
                <p className="text-gray-600">
                  {isSignUp ? 'Start making a difference today' : 'Continue your eco journey'}
                </p>
              </div>
              
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {isSignUp && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder="Choose a username"
                        required
                      />
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="Enter your password"
                      required
                    />
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>{isSignUp ? 'Creating Account...' : 'Signing In...'}</span>
                    </>
                  ) : (
                    <span>{isSignUp ? 'Create Account' : 'Sign In'}</span>
                  )}
                </button>
              </form>
              
              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  {isSignUp ? 'Already have an account?' : "Don't have an account?"}
                  <button
                    onClick={() => {
                      setIsSignUp(!isSignUp);
                      setError('');
                    }}
                    className="text-emerald-600 hover:text-emerald-700 font-medium ml-1"
                  >
                    {isSignUp ? 'Sign In' : 'Sign Up'}
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Real Impact, Real Numbers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our community is making a measurable difference. Join thousands of eco-warriors creating positive environmental change.
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {impactLoading ? (
              // Loading skeleton
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="text-center group">
                  <div className="bg-gray-200 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <div className="w-8 h-8 bg-gray-300 rounded"></div>
                  </div>
                  <div className="bg-gray-200 h-8 w-16 mx-auto mb-2 rounded animate-pulse"></div>
                  <div className="bg-gray-200 h-4 w-24 mx-auto rounded animate-pulse"></div>
                </div>
              ))
            ) : (
              displayStats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="text-center group">
                    <div className="bg-emerald-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-200 transition-colors">
                      <Icon className="h-8 w-8 text-emerald-600" />
                    </div>
                    <div className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                    <div className="text-gray-600 font-medium">{stat.label}</div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              How Yashnar Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simple, fun, and effective. Our platform makes environmental action accessible to everyone.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-2 border border-gray-100">
                  <div className="bg-emerald-100 w-12 h-12 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="h-6 w-6 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Your Eco Journey Timeline */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Your Eco Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Follow these simple steps to start making a positive environmental impact today
            </p>
          </div>

          {/* Desktop Timeline */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-emerald-200 via-blue-200 via-yellow-200 via-purple-200 to-green-200 rounded-full transform -translate-y-1/2"></div>
              
              {/* Timeline Steps */}
              <div className="relative flex justify-between items-center">
                {journeySteps.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <div key={step.id} className="flex flex-col items-center group">
                      {/* Step Circle */}
                      <div className={`relative w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300 mb-6`}>
                        <Icon className="h-8 w-8 text-white" />
                        <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md">
                          <span className="text-sm font-bold text-gray-700">{step.id}</span>
                        </div>
                      </div>
                      
                      {/* Step Content */}
                      <div className="text-center max-w-xs">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Mobile Timeline */}
          <div className="lg:hidden space-y-8">
            {journeySteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={step.id} className="flex items-start space-x-4">
                  {/* Step Circle */}
                  <div className={`flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg relative`}>
                    <Icon className="h-6 w-6 text-white" />
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md">
                      <span className="text-xs font-bold text-gray-700">{step.id}</span>
                    </div>
                  </div>
                  
                  {/* Step Content */}
                  <div className="flex-1 pt-2">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                  
                  {/* Connecting Line (except for last item) */}
                  {index < journeySteps.length - 1 && (
                    <div className="absolute left-8 mt-16 w-0.5 h-8 bg-gray-200"></div>
                  )}
                </div>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <button
              onClick={scrollToRegistration}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex items-center justify-center space-x-2 mx-auto"
            >
              <span>Start Your Journey Today</span>
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Mission Examples */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Sample Eco Missions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From simple daily habits to community organizing, every action counts toward a greener future.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {missionsLoading ? (
              // Loading skeleton
              Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-6 animate-pulse">
                  <div className="flex items-center justify-between mb-4">
                    <div className="bg-gray-200 p-2 rounded-lg w-10 h-10"></div>
                    <div className="text-right">
                      <div className="bg-gray-200 h-6 w-12 rounded mb-1"></div>
                      <div className="bg-gray-200 h-3 w-16 rounded"></div>
                    </div>
                  </div>
                  <div className="bg-gray-200 h-6 w-3/4 rounded mb-2"></div>
                  <div className="bg-gray-200 h-4 w-full rounded mb-4"></div>
                  <div className="flex items-center justify-between">
                    <div className="bg-gray-200 h-6 w-16 rounded"></div>
                    <div className="bg-gray-200 h-8 w-20 rounded"></div>
                  </div>
                </div>
              ))
            ) : (
              displayMissions.map((mission, index) => {
                const Icon = mission.icon;
                return (
                  <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-emerald-300 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`p-2 rounded-lg ${mission.color}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900">+{mission.points}</div>
                        <div className="text-xs text-gray-500">EcoCoins</div>
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{mission.title}</h3>
                    <p className="text-gray-600 mb-4">{mission.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                        {mission.difficulty}
                      </span>
                      <button 
                        onClick={scrollToRegistration}
                        className="text-emerald-600 hover:text-emerald-700 font-medium flex items-center space-x-1"
                      >
                        <span>Try Mission</span>
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What Our Community Says
            </h2>
            <p className="text-xl text-gray-600">
              Real stories from real eco-warriors making a difference
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className="flex items-center mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-gray-700 mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>
                
                <div className="flex items-center space-x-3">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Start Your Eco Journey?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
            Join {impactTypes.find(t => t.name === 'total_users')?.value ? 
              formatValue(impactTypes.find(t => t.name === 'total_users')!.value, '') : 
              '19'} eco-warriors making a real difference. Every small action contributes to a greener, more sustainable future.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToRegistration}
              className="bg-white hover:bg-gray-50 text-emerald-600 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:shadow-lg flex items-center justify-center space-x-2"
            >
              <span>Get Started Free</span>
              <ArrowRight className="h-5 w-5" />
            </button>
            
            <button 
              onClick={scrollToRegistration}
              className="border-2 border-white hover:bg-white hover:text-emerald-600 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300"
            >
              Learn More
            </button>
          </div>
          
          <div className="mt-8 flex items-center justify-center space-x-6 text-emerald-100">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5" />
              <span>Free to join</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5" />
              <span>Real rewards</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5" />
              <span>Measurable impact</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="bg-emerald-500 p-2 rounded-lg">
                  <Leaf className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Yashnar</h3>
                  <p className="text-sm text-gray-400">Gamify Your Impact</p>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed mb-6">
                Empowering millions to take small, consistent environmental actions that lead to measurable change in their communities and the planet.
              </p>
              <div className="flex space-x-4">
                <div className="bg-gray-800 p-2 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer" onClick={scrollToRegistration}>
                  <Heart className="h-5 w-5" />
                </div>
                <div className="bg-gray-800 p-2 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer" onClick={scrollToRegistration}>
                  <Users className="h-5 w-5" />
                </div>
                <div className="bg-gray-800 p-2 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer" onClick={scrollToRegistration}>
                  <Globe className="h-5 w-5" />
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Platform</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" onClick={scrollToRegistration} className="hover:text-white transition-colors">How it Works</a></li>
                <li><a href="#" onClick={scrollToRegistration} className="hover:text-white transition-colors">Missions</a></li>
                <li><a href="#" onClick={scrollToRegistration} className="hover:text-white transition-colors">Rewards</a></li>
                <li><a href="#" onClick={scrollToRegistration} className="hover:text-white transition-colors">Community</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" onClick={scrollToRegistration} className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" onClick={scrollToRegistration} className="hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="#" onClick={scrollToRegistration} className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" onClick={scrollToRegistration} className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Yashnar. All rights reserved. Made with 💚 for the planet.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}